import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Box, 
    CircularProgress,
    Typography,
    SelectChangeEvent,
    Button,
    Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { companyService } from '../services/companyService';

interface CompanyOption {
    id: number;
    name: string;
    tax_id?: string;
}

interface CompanySelectorProps {
    selectedCompanyId: number;
    onCompanyChange: (companyId: number) => void;
    label?: string;
    disabled?: boolean;
    includeAllOption?: boolean;
    companies?: CompanyOption[];
    loading?: boolean;
    error?: string | null;
    onRefresh?: () => void;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ 
    selectedCompanyId, 
    onCompanyChange, 
    label = "Select Company",
    disabled = false,
    includeAllOption = true,
    companies: companiesProp,
    loading: loadingProp,
    error: errorProp,
    onRefresh,
}) => {
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Function to fetch companies
    const fetchCompanies = async () => {
        // Skip internal fetch if caller is managing company list
        if (Array.isArray(companiesProp)) {
            setCompanies(companiesProp);
            setLoading(Boolean(loadingProp));
            setError(errorProp ?? null);
            return;
        }

        try {
            logger.log('CompanySelector: Fetching companies...');
            setLoading(true);
            setError(null);
            
            // Force bypass cache to ensure fresh data
            const data = await companyService.listCompanies();
            // logger.log('CompanySelector: Companies fetched:', data);
            
            setCompanies(data.map(company => ({
                id: company.id,
                name: company.name,
                tax_id: company.tax_id
            })));
            
            // If the selected company is not in the list, select the first company
            // Only auto-select if selectedCompanyId was explicitly set (not null/0)
            if (data.length > 0 && selectedCompanyId && !data.some(c => c.id === selectedCompanyId)) {
                logger.log('CompanySelector: Selected company not found, updating to first');
                onCompanyChange(data[0].id);
            } else if (data.length > 0 && !selectedCompanyId) {
                // Don't auto-select, let parent component handle initialization
                logger.log('CompanySelector: No company selected initially, waiting for parent');
            }
        } catch (err) {
            logger.error('CompanySelector: Error loading companies:', err);
            setError('Failed to load companies');
        } finally {
            setLoading(false);
        }
    };

    // Fetch companies on mount
    useEffect(() => {
        if (Array.isArray(companiesProp)) {
            setCompanies(companiesProp);
            setLoading(Boolean(loadingProp));
            setError(errorProp ?? null);
            return;
        }
        fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companiesProp, loadingProp, errorProp]);

    // Handle company selection change
    const handleChange = (event: SelectChangeEvent<number>) => {
        const newCompanyId = Number(event.target.value);
        logger.log('CompanySelector: Company selection changed to:', newCompanyId);
        onCompanyChange(newCompanyId);
    };

    // Handle refresh button click
    const handleRefresh = () => {
        logger.log('CompanySelector: Refreshing company list');
        if (onRefresh) {
            setLoading(true);
            setError(null);
            onRefresh();
            return;
        }
        fetchCompanies();
    };

    if (loading) {
        return (
            <Box display="flex" alignItems="center">
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Loading companies...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" alignItems="center">
                <Typography variant="body2" color="error" sx={{ mr: 1 }}>
                    {error}
                </Typography>
                <Button size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                    Retry
                </Button>
            </Box>
        );
    }

    // If no companies, show message
    if (companies.length === 0) {
        return (
            <Typography variant="body2" color="warning.main">
                No companies available
            </Typography>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <FormControl sx={{ minWidth: 250 }} size="small">
                <InputLabel>{label}</InputLabel>
                <Select
                    disabled={disabled}
                    value={selectedCompanyId || (includeAllOption ? 0 : '')}
                    label={label}
                    onChange={handleChange}
                >
                    {includeAllOption && (
                        <MenuItem value={0}>
                            All Companies
                        </MenuItem>
                    )}
                    {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                            {company.name} {company.tax_id && `(${company.tax_id})`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            
            <Tooltip title="Refresh company list">
                <Button 
                    size="small" 
                    onClick={handleRefresh} 
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <RefreshIcon fontSize="small" />
                </Button>
            </Tooltip>
        </Box>
    );
};

export default CompanySelector; 
